import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, UserPlus } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function TeamManager() {
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeams();
    fetchAgents();
  }, []);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/teams`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/agents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/teams`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTeams();
      setShowForm(false);
      setFormData({ name: '', description: '' });
    } catch (error) {
      console.error('Error creating team:', error);
      alert('Failed to create team');
    }
  };

  const handleAddMember = async (teamId, agentId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/teams/${teamId}/members/${agentId}`,
        { role: 'member' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTeams();
    } catch (error) {
      console.error('Error adding team member:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold flex items-center gap-2">
          <Users className="w-6 h-6" />
          Team Management
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Team
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="border rounded p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Team Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows={2}
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormData({ name: '', description: '' });
              }}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-4">Loading teams...</div>
      ) : teams.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No teams created yet</div>
      ) : (
        <div className="space-y-4">
          {teams.map((team) => (
            <div key={team.id} className="border rounded p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-semibold">{team.name}</h4>
                  {team.description && (
                    <p className="text-sm text-gray-600">{team.description}</p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedTeam(selectedTeam === team.id ? null : team.id)}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  {selectedTeam === team.id ? 'Hide Members' : 'View Members'}
                </button>
              </div>
              {selectedTeam === team.id && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium">Add Member:</span>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddMember(team.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="">Select agent...</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name || agent.email}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

